import { useMemo } from 'react'
import { z } from 'zod'
import { zodValidator } from '@tanstack/zod-adapter'
import { subDays } from 'date-fns'
import type { DateRange } from 'react-day-picker'
import { createFileRoute } from '@tanstack/react-router'
import { PageHeader } from '@/components/shared/page-header'
import { AlertCircle } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { useAnalytics, useCampaigns } from '@/hooks'
import type { AnalyticsSummary } from '@/lib/api'

import { HeroSection } from '@/components/analytics/hero-section'
import { WaterfallSection } from '@/components/analytics/waterfall-section'
import { RevenueAreaSection } from '@/components/analytics/revenue-area-section'
import { CohortSection } from '@/components/analytics/cohort-section'
import { UnitEconomicsSection } from '@/components/analytics/unit-economics-section'
import {
  AnalyticsFilters,
  DEFAULT_PERIOD,
  type PeriodValue,
} from '@/components/analytics/analytics-filters'

const PERIOD_DAYS: Record<PeriodValue, number | null> = {
  '7d': 7,
  '30d': 30,
  '90d': 90,
  all: null,
}

const searchSchema = z.object({
  campaignId: z.string().optional(),
  period: z.enum(['7d', '30d', '90d', 'all']).optional(),
  startDate: z.string().optional(),
  endDate: z.string().optional(),
  excludeRepurchases: z.boolean().optional(),
})

export const Route = createFileRoute('/_app/analytics')({
  component: AnalyticsPage,
  validateSearch: zodValidator(searchSchema),
})

// ─── Page ───────────────────────────────────────────────────────────────────

function AnalyticsPage() {
  const search = Route.useSearch()
  const navigate = Route.useNavigate()

  // Active period: from URL, or default if no custom dates
  const hasCustomDates = !!(search.startDate && search.endDate && !search.period)
  const activePeriod: PeriodValue | undefined = hasCustomDates
    ? undefined
    : (search.period ?? DEFAULT_PERIOD)

  // Compute effective dates from period or custom range
  const effectiveDates = useMemo(() => {
    if (hasCustomDates) {
      return { startDate: search.startDate, endDate: search.endDate }
    }
    const period = activePeriod ?? DEFAULT_PERIOD
    if (period === 'all') return {}
    const days = PERIOD_DAYS[period]!
    const end = new Date()
    const start = subDays(end, days - 1)
    return { startDate: start.toISOString(), endDate: end.toISOString() }
  }, [hasCustomDates, search.startDate, search.endDate, activePeriod])

  // Only show dates in picker when using custom range, not presets
  const dateRange: DateRange | undefined =
    hasCustomDates && effectiveDates.startDate && effectiveDates.endDate
      ? { from: new Date(effectiveDates.startDate), to: new Date(effectiveDates.endDate) }
      : undefined

  const selectedCampaign = search.campaignId ?? 'all'

  const excludeRepurchases = search.excludeRepurchases ?? false

  const filters = {
    startDate: effectiveDates.startDate,
    endDate: effectiveDates.endDate,
    campaignId: search.campaignId,
    excludeRepurchases: excludeRepurchases || undefined,
  }

  const handlePeriodChange = (period: PeriodValue) => {
    navigate({
      search: (prev) => ({
        campaignId: prev.campaignId,
        excludeRepurchases: prev.excludeRepurchases,
        period,
        startDate: undefined,
        endDate: undefined,
      }),
    })
  }

  const handleDateChange = (range: DateRange | undefined) => {
    navigate({
      search: (prev) => ({
        campaignId: prev.campaignId,
        excludeRepurchases: prev.excludeRepurchases,
        period: undefined,
        startDate: range?.from?.toISOString(),
        endDate: range?.to?.toISOString(),
      }),
    })
  }

  const handleCampaignChange = (val: string) => {
    navigate({
      search: (prev) => ({
        ...prev,
        campaignId: val === 'all' ? undefined : val,
      }),
    })
  }

  const handleToggleRepurchases = () => {
    navigate({
      search: (prev) => ({
        ...prev,
        excludeRepurchases: !excludeRepurchases || undefined,
      }),
    })
  }

  const hasActiveFilters = selectedCampaign !== 'all' || hasCustomDates || excludeRepurchases
  const handleClearFilters = () => navigate({ search: {} })

  const { data, isLoading, error, refetch } = useAnalytics(filters)
  const { data: campaigns } = useCampaigns()

  if (error) {
    return (
      <div className="space-y-8">
        <PageHeader
          title="Analytics"
          description="ROI e saúde financeira do programa de indicações"
        />
        <div className="flex flex-col items-center justify-center py-16 text-center">
          <AlertCircle className="h-10 w-10 text-muted-foreground/50 mb-4" />
          <p className="text-base font-semibold">Erro ao carregar analytics</p>
          <p className="text-sm text-muted-foreground mt-1 mb-4">
            Algo deu errado. Tente novamente em alguns instantes.
          </p>
          <Button variant="outline" onClick={() => refetch()}>
            Tentar novamente
          </Button>
        </div>
      </div>
    )
  }

  const emptySummary: AnalyticsSummary = {
    receita1aCompra: 0,
    receitaRecompras: 0,
    receitaTotal: 0,
    descontoConcedido: 0,
    cashbackResgatado: 0,
    custoReal: 0,
    resultadoLiquido: 0,
    roi: 0,
    passivoPendente: 0,
    passivoDisponivel: 0,
  }

  return (
    <div className="space-y-8">
      <PageHeader
        title="Analytics"
        description="ROI e saúde financeira do programa de indicações"
        actions={
          <AnalyticsFilters
            activePeriod={activePeriod}
            selectedCampaign={selectedCampaign}
            dateRange={dateRange}
            excludeRepurchases={excludeRepurchases}
            hasActiveFilters={hasActiveFilters}
            campaigns={campaigns}
            onPeriodChange={handlePeriodChange}
            onCampaignChange={handleCampaignChange}
            onDateChange={handleDateChange}
            onToggleRepurchases={handleToggleRepurchases}
            onClearFilters={handleClearFilters}
          />
        }
      />

      {/* Section 1: Hero */}
      <HeroSection
        summary={data?.summary ?? emptySummary}
        previous={data?.summaryPrevious ?? null}
        timeSeries={data?.timeSeries ?? []}
        isLoading={isLoading}
      />

      {/* Unit Economics */}
      <UnitEconomicsSection
        unitEconomics={
          data?.unitEconomics ?? {
            cacReal: 0,
            ltvMedio: 0,
            ltvCacRatio: 0,
            taxaAprovacao: 0,
            taxaFraude: 0,
            taxaAtivacao: 0,
          }
        }
        isLoading={isLoading}
      />

      {/* Section 2 + 3: Waterfall + Area Chart */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <WaterfallSection summary={data?.summary ?? emptySummary} isLoading={isLoading} />
        <RevenueAreaSection timeSeries={data?.timeSeries ?? []} isLoading={isLoading} />
      </div>

      {/* Section 4: Cohort */}
      <CohortSection cohorts={data?.cohorts ?? []} isLoading={isLoading} />
    </div>
  )
}
